import { Grid, Flex, Button } from '@wegroup/design-system';
import React from 'react';
import { LouiseOffer } from '../../../../../../types/Quotes';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import {
  addToQuoteSpecifications,
  QuoteSpecifications,
} from '../../../store/redux/slices/offersSlice';
import { useForm, FormProvider } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import OfferDiscountFormControls from './OfferDiscountFormControls';
import OfferHomeFormControls from './OfferHomeFormControls';
import { toEuro } from '../../../utils/stringUtils';
import { RootState } from '../../../store/redux/reducers/rootReducer';
import OfferCarFormControls from './OfferCarFormControls';
import { transformFranchisesToOptions } from '../../../../../tarifications/src/legacy/services/proFlowService';
import { Franchise } from '../../../../../../types/Offer';
import OfferGeneralFormControls from './OfferGeneralFormControls';
import { defaultPaymentIntervals } from '../../selected-offers/single-selected-offers/SingleSelectedOfferForm';
import { getProductKey } from '../../../services/offersService';

interface Props {
  maxVk?: number;
  maxCk: number;
  maxInsuredValue?: number;
  maxInsuredContentValue?: number;
  franchises?: Franchise[];
  intervals?: string[];
  offer: LouiseOffer;
  onClose: () => void;
}

export interface OffersParametersFormType {
  vk: string;
  ck: string;
  insuredValue: number;
  insuredContentValue: number;
  franchise: Franchise;
  paymentInterval: string;
  evaluationBuilding: string;
}

const OfferParametersForm: React.FC<React.PropsWithChildren<Props>> = ({
  maxVk,
  maxCk,
  maxInsuredValue,
  maxInsuredContentValue,
  franchises,
  intervals,
  offer,
  onClose,
}) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const vk = useSelector(
    (state: RootState) => state.offers.quoteSpecs[getProductKey(offer)]?.vk,
  );
  const ck = useSelector(
    (state: RootState) => state.offers.quoteSpecs[getProductKey(offer)]?.ck,
  );
  const insuredValue = useSelector(
    (state: RootState) =>
      state.offers.quoteSpecs[getProductKey(offer)]?.insured_value,
  );
  const insuredContentValue = useSelector(
    (state: RootState) =>
      state.offers.quoteSpecs[getProductKey(offer)]?.insured_content_value,
  );
  const franchise = useSelector(
    (state: RootState) =>
      state.offers.quoteSpecs[getProductKey(offer)]?.franchise,
  );
  const paymentInterval = useSelector(
    (state: RootState) =>
      state.offers.quoteSpecs[getProductKey(offer)]?.payment_interval,
  );
  const evaluationBuilding = useSelector(
    (state: RootState) =>
      state.offers.quoteSpecs[getProductKey(offer)]?.evaluation_building,
  );

  const minEuroErrorMessage = t('Minimum value is x', { x: toEuro(0) });
  const minPercentageErrorMessage = t('Minimum value is x', { x: 0 }) + '%';

  const schema = yup.object().shape({
    ...(maxVk && {
      vk: yup
        .number()
        .typeError(minPercentageErrorMessage)
        .min(0, minPercentageErrorMessage)
        .max(maxVk, t('Maximum value is x', { x: maxVk }) + '%'),
    }),
    ck: yup
      .number()
      .typeError(minPercentageErrorMessage)
      .min(0, minPercentageErrorMessage)
      .max(maxCk, t('Maximum value is x', { x: maxCk }) + '%'),
    ...(maxInsuredValue && {
      insuredValue: yup
        .number()
        .typeError(minEuroErrorMessage)
        .min(0, minEuroErrorMessage)
        .max(
          maxInsuredValue,
          t('Maximum value is x', { x: toEuro(maxInsuredValue) }),
        ),
    }),
    ...(maxInsuredContentValue && {
      insuredContentValue: yup
        .number()
        .typeError(minEuroErrorMessage)
        .min(0, minEuroErrorMessage)
        .max(
          maxInsuredContentValue,
          t('Maximum value is x', { x: toEuro(maxInsuredContentValue) }),
        ),
    }),
  });

  const formMethods = useForm<OffersParametersFormType>({
    defaultValues: {
      vk: ((vk || offer.details?.vk || 0) * 100).toString(),
      ck: ((ck || offer.details?.ck || 0) * 100).toString(),
      insuredValue:
        insuredValue ||
        parseFloat(offer.details?.insured_value?.value?.toFixed(2) || '0'),
      insuredContentValue:
        insuredContentValue ||
        parseFloat(
          offer.details?.insured_content_value?.value?.toFixed(2) || '0',
        ),
      franchise: franchise || offer.details?.franchise,
      paymentInterval:
        paymentInterval || offer.details?.payment_interval || 'annual',
      evaluationBuilding:
        evaluationBuilding ||
        offer.details?.insured_value?.evaluation_type ||
        'LOCATION_SYSTEM',
    },
    resolver: yupResolver(schema),
  });
  const onSubmit = formMethods.handleSubmit((data) => {
    const { touchedFields } = formMethods.formState;
    const newQuoteSpecs: QuoteSpecifications = {};

    if (data.paymentInterval != null && touchedFields.paymentInterval)
      newQuoteSpecs.payment_interval = data.paymentInterval || 'annual';

    if (data.vk != null && touchedFields.vk)
      newQuoteSpecs.vk = data.vk
        ? Number((parseFloat(data.vk) / 100).toFixed(4))
        : 0;
    if (data.ck != null && touchedFields.ck)
      newQuoteSpecs.ck = data.ck
        ? Number((parseFloat(data.ck) / 100).toFixed(4))
        : 0;

    if (offer.info.insurance_type === 'home') {
      if (data.evaluationBuilding != null && touchedFields.evaluationBuilding)
        newQuoteSpecs.evaluation_building =
          data.evaluationBuilding || 'LOCATION_SYSTEM';

      if (
        data.insuredValue != null &&
        touchedFields.insuredValue &&
        data.evaluationBuilding === 'FIRST_RISK'
      )
        newQuoteSpecs.insured_value = data.insuredValue;

      if (data.insuredContentValue && touchedFields.insuredContentValue)
        newQuoteSpecs.insured_content_value = data.insuredContentValue;
    }

    if (offer.info.insurance_type === 'car') {
      if (data.franchise != null && touchedFields.franchise)
        newQuoteSpecs.franchise = data.franchise;
    }

    dispatch(
      addToQuoteSpecifications({
        productKey: getProductKey(offer),
        quoteId: offer.hash_id,
        value: newQuoteSpecs,
      }),
    );

    onClose();
  });

  const intervalOptions = intervals
    ? intervals.map((value) => ({
        label: t(`spread.${value.toUpperCase()}`),
        value: value,
      }))
    : defaultPaymentIntervals.map((value) => ({
        label: t(`spread.${value.toUpperCase()}`),
        value: value,
      }));

  const evaluationSystemTypes = [
    { value: 'LOCATION_SYSTEM', label: t('LOCATION_SYSTEM') },
    { value: 'FIRST_RISK', label: t('FIRST_RISK') },
    { value: 'X20_ANNUAL_RENT', label: t('X20_ANNUAL_RENT') },
    { value: 'PROPORTIONALITY_RULE', label: t('PROPORTIONALITY_RULE') },
    ...((offer.info.company?.key?.toLowerCase() === 'axa' && [
      { value: 'ITEM_LIMIT', label: t('ITEM_LIMIT') },
    ]) ||
      []),
    { value: 'OTHER', label: t('OTHER') },
  ];

  return (
    <FormProvider {...formMethods}>
      <form onSubmit={onSubmit}>
        <Grid templateColumns="repeat(2, 224px)" gap="4">
          <OfferDiscountFormControls withVk={maxVk !== 0} />
          <OfferGeneralFormControls options={intervalOptions} />
          {offer.info.insurance_type === 'home' && (
            <OfferHomeFormControls
              // If a max is given this means that this value can be changed
              withContentValue={!!maxInsuredContentValue}
              evaluationSystemTypes={evaluationSystemTypes}
            />
          )}
          {offer.info.insurance_type === 'car' &&
            offer.details?.franchise != null && (
              <OfferCarFormControls
                options={transformFranchisesToOptions(
                  franchises || offer.details?.franchises,
                )}
              />
            )}
        </Grid>
        <Flex mt="2" justify="flex-end">
          <Button
            type="submit"
            variant="primary"
            isLoading={formMethods.formState.isSubmitting}
          >
            {t('Apply')}
          </Button>
        </Flex>
      </form>
    </FormProvider>
  );
};

export default OfferParametersForm;
